import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IWebsiteBuilderNodePosition } from "../../../../../nodes/websiteBuilderNode";
import { TWebsiteBuilderTreeRefMap } from "../../../../../hooks/treeRefMap/useTreeRefMapRegistration";
import { clamp } from "lodash";
import { useAppDispatch } from "hooks/redux";
import {
  websiteBuilderMoveElement,
  websiteBuilderUpdateSelectedNode,
} from "redux/actions/websiteBuilder/websiteBuilder";
import { WEBSITE_BUILDER_SECTION_CHILD_PLACEMENT_BOUNDARY as SECTION_CHILD_PLACEMENT_BOUNDARY } from "../../../../../nodes/section/websiteBuilderSection";
import {
  getElementPosition,
  IWebsiteBuilderElementStretch,
} from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderElement";
import { ElementViewIndicator, TElementViewIndicator } from "../helpers/ElementViewIndicator";
import { IWebsiteBuilderDesignSpaceBoundaries } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";

export interface IUseElementViewMoveArgs {
  id: string;
  parentId: string;
  position: IWebsiteBuilderNodePosition;
  screenPosition: IWebsiteBuilderNodePosition;
  moving: boolean;
  designSpaceBoundaries: IWebsiteBuilderDesignSpaceBoundaries;
  stretch: IWebsiteBuilderElementStretch;
  treeRefMap: TWebsiteBuilderTreeRefMap;
}

interface IElementViewMoveRefState {
  prevPosition: IWebsiteBuilderNodePosition;
  nextPosition: IWebsiteBuilderNodePosition;
  mouseOffset: IWebsiteBuilderNodePosition;
  moving: boolean;
}

interface IElementViewMoveState {
  allowed: boolean;
  position: IWebsiteBuilderNodePosition;
  screenPosition: IWebsiteBuilderNodePosition;
}

type THandleElementViewMovePointerDown = (e: React.PointerEvent<HTMLDivElement>) => void;

interface IUseElementViewMove {
  viewMoveScreenPosition: IWebsiteBuilderNodePosition;
  viewMoveIndicator: JSX.Element;
  handleViewMovePointerDown: THandleElementViewMovePointerDown;
}

export function useElementViewMove(args: IUseElementViewMoveArgs): IUseElementViewMove {
  const dispatch = useAppDispatch();

  const moveRefState = useRef<IElementViewMoveRefState>({
    prevPosition: null,
    nextPosition: null,
    mouseOffset: null,
    moving: false,
  });

  const [moveState, setMoveState] = useState<IElementViewMoveState>({
    allowed: false,
    position: null,
    screenPosition: null,
  });

  const indicator: TElementViewIndicator = useMemo(() => {
    if (args.moving) {
      const indicatorPosition = args.stretch?.enabled ? moveState.screenPosition : moveState.position;

      return {
        type: "position",
        position: indicatorPosition,
      };
    } else {
      return null;
    }
  }, [args.moving, args.stretch?.enabled, moveState.screenPosition, moveState.position]);

  const viewMoveScreenPosition: IWebsiteBuilderNodePosition = useMemo(() => {
    if (args.moving && moveState.screenPosition) {
      return moveState.screenPosition;
    } else {
      return args.screenPosition;
    }
  }, [args.moving, moveState.screenPosition, args.screenPosition]);

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const { mouseOffset, moving } = moveRefState.current;

      const ref = args.treeRefMap.current.get(args.id);
      const parentRef = args.treeRefMap.current.get(args.parentId);

      const boundingRect = ref?.getBoundingClientRect();
      const parentBoundingRect = parentRef?.getBoundingClientRect();

      if (!boundingRect || !parentBoundingRect || !mouseOffset) {
        return;
      }

      const mousePosition: IWebsiteBuilderNodePosition = {
        x: e.clientX,
        y: e.clientY,
      };

      const updatedScreenPosition: IWebsiteBuilderNodePosition = {
        x: mousePosition.x - mouseOffset.x - parentBoundingRect.x,
        y: mousePosition.y - mouseOffset.y - parentBoundingRect.y,
      };

      const clampedUpdatedScreenPosition: IWebsiteBuilderNodePosition = {
        x: clamp(
          updatedScreenPosition.x,
          args.designSpaceBoundaries.leftX + SECTION_CHILD_PLACEMENT_BOUNDARY,
          args.designSpaceBoundaries.rightX - boundingRect.width - SECTION_CHILD_PLACEMENT_BOUNDARY,
        ),
        y: clamp(
          updatedScreenPosition.y,
          SECTION_CHILD_PLACEMENT_BOUNDARY,
          parentBoundingRect.height - boundingRect.height - SECTION_CHILD_PLACEMENT_BOUNDARY,
        ),
      };

      const nextScreenPosition: IWebsiteBuilderNodePosition = {
        x: clampedUpdatedScreenPosition.x,
        y: clampedUpdatedScreenPosition.y,
      };

      const nextPosition = getElementPosition(args.designSpaceBoundaries.leftX, nextScreenPosition);

      if (args.stretch?.enabled) {
        nextScreenPosition.x = args.stretch.margin;
        nextPosition.x = SECTION_CHILD_PLACEMENT_BOUNDARY;
      }

      moveRefState.current.nextPosition = nextPosition;

      setMoveState(prev => ({
        ...prev,
        position: nextPosition,
        screenPosition: nextScreenPosition,
      }));

      if (!moving) {
        moveRefState.current.moving = true;

        dispatch(
          websiteBuilderUpdateSelectedNode({
            selectedNodeUpdates: {
              editingMode: "element-move",
            },
          }),
        );
      }
    },
    [args.id, args.parentId, args.designSpaceBoundaries.leftX, args.designSpaceBoundaries.rightX, args.stretch],
  );

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      const { moving, prevPosition, nextPosition } = moveRefState.current;

      if (moving) {
        if (
          prevPosition &&
          nextPosition &&
          (Math.abs(prevPosition.x - nextPosition.x) > 1 || Math.abs(prevPosition.y - nextPosition.y) > 1)
        ) {
          dispatch(
            websiteBuilderMoveElement({
              id: args.id,
              prevPosition: { ...prevPosition },
              nextPosition: { ...nextPosition },
            }),
          );
        } else {
          dispatch(
            websiteBuilderUpdateSelectedNode({
              selectedNodeUpdates: {
                editingMode: "none",
              },
            }),
          );
        }
      }

      const ref = args.treeRefMap.current.get(args.id);

      ref?.releasePointerCapture(e.pointerId);

      moveRefState.current = {
        prevPosition: null,
        nextPosition: null,
        mouseOffset: null,
        moving: false,
      };

      setMoveState(prev => ({
        ...prev,
        allowed: false,
        position: null,
        screenPosition: null,
      }));
    },
    [args.id],
  );

  useEffect(() => {
    if (moveState.allowed) {
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
    }

    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [moveState.allowed, handlePointerMove, handlePointerUp]);

  const handleViewMovePointerDown: THandleElementViewMovePointerDown = e => {
    const ref = args.treeRefMap.current.get(args.id);

    if (ref) {
      ref.setPointerCapture(e.pointerId);

      moveRefState.current.mouseOffset = {
        x: e.clientX - ref.getBoundingClientRect().x,
        y: e.clientY - ref.getBoundingClientRect().y,
      };

      moveRefState.current.prevPosition = { ...args.position };
      moveRefState.current.nextPosition = { ...args.position };

      moveRefState.current.moving = false;

      setMoveState(prev => ({
        ...prev,
        allowed: true,
      }));
    }
  };

  return {
    viewMoveScreenPosition,
    viewMoveIndicator: <ElementViewIndicator indicator={indicator} />,
    handleViewMovePointerDown,
  };
}
